import { cn } from '../../lib/cn'
import { formatPlayedOn, formatToPar, formatWeekday } from '../../lib/golfFormat'
import { romanYear } from '../../lib/roman'
import { Container } from './Container'
import { Crest } from './Crest'
import { Eyebrow } from './Eyebrow'

/** A line on the postcard: who, what it cost, and against what. */
export interface ShareHeroPlayer {
  id: string
  displayName: string
  /** `total_strokes` — the gross. Null on a card that was never totalled. */
  gross: number | null
  /** `summary.to_par` — null where the club has no par to compare against. */
  toPar: number | null
}

export interface ShareHeroProps {
  courseName: string
  /** `played_on` — DATE-ONLY, and parsed as such (see `formatPlayedOn`). */
  playedOn: string | null
  /** 9 or 18 — the holes the round PLAYED, which the foot line names. */
  holesCount: number
  /**
   * IN STANDING ORDER, lowest gross first — the first row is drawn as the
   * lead. Ordering is the caller's because the same standing feeds the
   * scoreboard band further down the page, and two components sorting the same
   * four players by the same rule is one rule too many.
   */
  players: readonly ShareHeroPlayer[]
  className?: string
}

/**
 * THE POSTCARD — the opening of a shared round, and the maintainer's binding
 * decision for this page (decision record §4, 2026-08-22 —
 * `../../../../../../docs/fireside/club-web-decisions.md`: *"share page:
 * postcard first, detail scrolls beneath"*).
 *
 * ## It is sized to BE the screenshot
 *
 * Someone opens this link from a message, looks at one thing, and either
 * scrolls or does not. So the card is a self-contained object — pine ground,
 * gilt frame, the seal, the day, and the four scores — that says the whole
 * afternoon at a glance and survives being cropped out of the page and sent on
 * again. Everything under it is for the reader who stayed.
 *
 * ## Gilt is legal HERE and nowhere else on the page
 *
 * The club's brass family fails AA as text on paper (3.38:1) and gilt is
 * lighter still. On PINE, gilt is 5.2:1 and passes — so the pine ground is the
 * one place the club writes in gilt, and this card is the only pine ground on
 * the share page. Sage (4.7:1 on pine) carries the to-par column and the foot;
 * parchment carries the names and the scores. No brass anywhere: it is a rule
 * colour, not an ink.
 *
 * ## The picture is never the only copy of the information
 *
 * The contour lines are decoration and are `aria-hidden`. Everything the card
 * SAYS — the course, the day, every name and every score — is text in the DOM,
 * in reading order, and every one of those numbers is also on the scorecard
 * below. The card asserts nothing the club did not compute: a player with no
 * total prints "—", and a round with no par prints "—" against par rather than
 * a flattering "E".
 *
 * The `h1` lives here rather than in the screen, and that is load-bearing:
 * `usePageTitle` moves focus to `main h1` on every route change, and the
 * postcard's title is what this page is about.
 */
export function ShareHero({
  courseName,
  playedOn,
  holesCount,
  players,
  className,
}: ShareHeroProps) {
  const weekday = formatWeekday(playedOn)

  return (
    <Container className={cn('pb-2 pt-9', className)}>
      <article
        data-share-postcard=""
        className="on-pine mx-auto max-w-[640px] rounded-club-lg bg-pine p-2.5"
      >
        <div className="relative overflow-hidden rounded-club border border-gilt px-5 pb-6 pt-7 text-center sm:px-7">
          <svg
            viewBox="0 0 640 420"
            preserveAspectRatio="none"
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 size-full"
          >
            <path
              d="M0,340 C140,318 260,362 420,344 C540,330 590,352 640,338"
              fill="none"
              stroke="var(--color-gilt)"
              strokeOpacity=".22"
            />
            <path
              d="M0,376 C160,356 300,396 460,380 C560,370 600,388 640,376"
              fill="none"
              stroke="var(--color-sage)"
              strokeOpacity=".18"
            />
          </svg>

          <Crest tone="gilt" monogram className="relative mx-auto mb-3.5 size-13" />

          <Eyebrow tone="gilt" className="relative">
            A round at the club
          </Eyebrow>

          {/* No date, no weekday: "A round at Kissena Park" rather than a
              sentence with a hole where the day was. */}
          <h1 className="relative mt-2 font-display text-[clamp(1.75rem,5.6vw,2.375rem)] font-medium leading-[1.08] tracking-[-0.01em] text-parchment">
            {weekday ? `${weekday} at ${courseName}` : `A round at ${courseName}`}
          </h1>

          {playedOn && (
            <p className="relative mt-1.5 text-[10.5px] font-semibold uppercase tracking-[0.26em] text-gilt">
              {formatPlayedOn(playedOn)} · {romanYear(Number(playedOn.slice(0, 4)))}
            </p>
          )}

          <ol
            data-share-standing=""
            className="relative mx-auto mt-5 grid max-w-[340px] gap-1.5 text-left"
          >
            {players.map((player, index) => (
              <li
                key={player.id}
                data-share-standing-row={player.id}
                className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-t border-sage-soft/25 pt-1.5 tabular-nums"
              >
                {/* The standing's rank, in the club's own numerals — I, II,
                    III, IV. NOT the seat: the postcard prints who finished
                    where, and the seat order is what the scorecard keeps. */}
                <span className="w-5 shrink-0 font-display text-[11px] text-gilt">
                  {romanYear(index + 1)}
                </span>
                <span
                  className={cn(
                    'min-w-0 flex-[1_1_120px] text-[14.5px]',
                    index === 0 ? 'font-semibold text-parchment' : 'text-paper',
                  )}
                >
                  {player.displayName}
                </span>
                <span
                  className={cn(
                    'font-display text-[19px]',
                    index === 0 ? 'font-semibold text-parchment' : 'text-paper',
                  )}
                >
                  {player.gross ?? '—'}
                </span>
                <span className="w-9 shrink-0 text-right text-[11.5px] text-sage">
                  {formatToPar(player.toPar)}
                </span>
              </li>
            ))}
          </ol>

          <p className="relative mt-5 text-[9px] font-semibold uppercase tracking-[0.24em] text-sage">
            {holesLine(holesCount)}
          </p>
        </div>
      </article>

      <p className="mx-auto mt-2.5 max-w-[640px] text-center text-[11.5px] text-ink-soft">
        Shared from the club — the whole round is below. No account asked of you.
      </p>
    </Container>
  )
}

/**
 * "Eighteen holes · carded in the club's book", and the nine's honest
 * equivalent.
 *
 * The club plays nines and eighteens (`Round::HOLE_COUNTS`), so those are the
 * two the club has a word for; anything else is counted rather than named,
 * because a spelled-out number the club invented would read as a claim.
 */
function holesLine(holesCount: number): string {
  const spelled =
    holesCount === 18 ? 'Eighteen holes' : holesCount === 9 ? 'Nine holes' : `${holesCount} holes`
  return `${spelled} · carded in the club’s book`
}
